import {NotificationManager} from 'react-notifications';
import {all, call, fork, put, take, takeEvery} from 'redux-saga/effects';
import {setContactUsDialogMessage, toggleDefaultTemplateDialogOpen} from '../actions';
import {changeDataValidity} from '../actions/RealtimeActions';
import {LOGIN_USER_SUCCESS, LOGOUT_USER_SUCCESS, SIGNUP_USER_SUCCESS} from '../actions/types';
import {
    accessTokenLocalStorageKey,
    dataIsValidAffectedIds,
    dataIsValidAfterDelete,
    dataIsValidAfterSave,
    realtimeSagaAfterDeleteAction,
    realtimeSagaAfterSaveAction,
    realtimeSagaExportEndedAction,
    realtimeSagaRecognitionLimitExceededAction,
    realtimeSagaToggleDefaultTemplateDialog,
} from '../constants';
import storage from '../fetchApi/storage';
import {createChannel} from './RealtimeChannel';
import {io} from "socket.io-client";
import {config} from "../config";

let channel = null;
let socket = null;

const dataValidityAction = (jobs) => {
    const {type} = jobs;
    jobs.payload = {};

    switch (type) {
        case realtimeSagaAfterSaveAction:
            jobs.payload = {...jobs.payload, [dataIsValidAfterSave]: false, [dataIsValidAffectedIds]: jobs.ids, documentType: jobs.documentType};
            break;
        case realtimeSagaAfterDeleteAction:
            jobs.payload = {...jobs.payload, [dataIsValidAfterDelete]: false, documentType: jobs.documentType};
            break;
        default:
            jobs.payload = {[dataIsValidAfterDelete]: true, [dataIsValidAfterSave]: true, [dataIsValidAffectedIds]: []};
            break;
    }

    return changeDataValidity(jobs);
};

const processExport = ({payload}) => {
    const {meta, status, documentId, provider, userId, exportType} = payload;

    const accessToken = storage.load(accessTokenLocalStorageKey);
    if (!accessToken || !accessToken.userId || accessToken.userId !== userId) {
        return;
    }

    if (status >= 400) {
        const failedStage = {};
        for (const [stageName, stageValue] of Object.entries(meta)) {
            if (stageValue.status === 'Failed') {
                failedStage.name = stageName;
                failedStage.message = stageValue.error;
                break;
            }
        }

        if (failedStage) {
            const message = `Failed to export document ${documentId} to ${provider} as ${exportType}. ` +
                `Stage "${failedStage.name}" has failed, message: ${failedStage.message}`;
            NotificationManager.error(message, '', 1000000); // TODO: change?
        } else {
            NotificationManager.error(`Failed to export document ${documentId} to ${provider} as ${exportType}`, '', 10000);
        }
    } else {
        NotificationManager.success(`Document ${documentId} successfully exported to ${provider} as ${exportType}`, '', 10000);
    }
};

const exportAction = (jobs) => {
    const {type, ...rest} = jobs;
    switch (type) {
        case realtimeSagaExportEndedAction:
            return processExport({payload: rest});
        default:
            return;
    }
};

const toggleDefaultTemplateDialog = (jobs) => {
    const userId = jobs && jobs.userId;

    const accessToken = storage.load(accessTokenLocalStorageKey);
    if (!userId || !accessToken || !accessToken.userId || accessToken.userId !== userId) {
        return;
    }

    return toggleDefaultTemplateDialogOpen(true);
};

const actionsMapping = {
    [realtimeSagaExportEndedAction]: exportAction,
    [realtimeSagaAfterDeleteAction]: dataValidityAction,
    [realtimeSagaAfterSaveAction]: dataValidityAction,
    [realtimeSagaRecognitionLimitExceededAction]: setContactUsDialogMessage,
    [realtimeSagaToggleDefaultTemplateDialog]: toggleDefaultTemplateDialog,
};

export function* subscribe() {
    try {
        const token = storage.loadItem(accessTokenLocalStorageKey);

        if (token && (!socket || socket.disconnect)) {
            socket = io(config.socketApi().url, {
                transports: ['websocket'],
                path: config.socketApi().path,
                auth: {
                    params: storage.loadItem('lbtoken'),
                },
            });
        }

        channel = yield call(createChannel, socket);
    } catch (e) {
        console.error(`Failed to establish socket connection: ${e}`);
    }

    while (channel && socket) {
        const jobs = yield take(channel);
        const {type} = jobs;

        const actionFunction = actionsMapping[type];
        if (actionFunction) {
            const action = actionFunction(jobs);
            if (action) {
                // console.log('!!!! action', action);
                yield put(action);
            }
        }
    }
}

function* getLogins() {
    yield takeEvery([LOGIN_USER_SUCCESS, SIGNUP_USER_SUCCESS], subscribe);
}

function* unsubscribe() {
    if (channel) {
        try {
            channel.close();
            channel = null;
        } catch (e) {
            console.error('Failed to close channel', e);
        }
    }

    if (socket && socket.connected) {
        socket.close();
        socket = null;
    }
}

function* getLogouts() {
    yield takeEvery([LOGOUT_USER_SUCCESS], unsubscribe);
}

export default function* rootSaga() {
    yield all([
        fork(subscribe),
        fork(getLogins),
        fork(getLogouts),
    ]);
}